"use client"

import { useEffect } from "react"

import { Button } from "@workspace/ui/components/button"
import { useTranslations } from "next-intl"

import { reportClientError } from "@/lib/report-client-error"

/**
 * App-shell error boundary for the (store) group (US-011, hole B). Catches errors
 * thrown by pages within the group (e.g. a failed data fetch) and shows a clear,
 * translated message with a retry — never a blank screen. The root layout renders
 * above this boundary, so next-intl's provider is available for `useTranslations`.
 * (Catastrophic root-layout errors fall through to app/global-error.tsx instead.)
 */
export default function StoreError({
  error,
  retry,
}: {
  error: Error & { digest?: string }
  /** Re-fetches + re-renders the failed segment (Next 16.3+). */
  retry: () => void
}) {
  const t = useTranslations("error")

  useEffect(() => {
    console.error(error)
    // Central observability path (US-039) — never per-component reporting.
    reportClientError(error, "store")
  }, [error])

  return (
    <main
      id="main-content"
      tabIndex={-1}
      className="flex min-h-svh flex-col items-center justify-center p-6 text-center"
    >
      {/* Client boundary — can't read settings, so the classic-backdrop
          panel keys off the `data-layout` seam attr on an ancestor. */}
      <div className="flex flex-col items-center gap-4 in-data-[layout=classic]:rounded-[var(--radius)] in-data-[layout=classic]:border in-data-[layout=classic]:border-border/60 in-data-[layout=classic]:bg-background/80 in-data-[layout=classic]:p-8 in-data-[layout=classic]:backdrop-blur">
        <h1 className="text-2xl font-semibold">{t("title")}</h1>
        <p className="max-w-prose text-sm text-muted-foreground">
          {t("description")}
        </p>
        <Button onClick={() => retry()}>{t("retry")}</Button>
      </div>
    </main>
  )
}
